import { type LifecycleStore } from "../store-contract";
import { deleteRowsWhere, getAllRows, txDone } from "./db";

// Profile lifecycle over the whole database: the purge is the deliberate end
// of a profile's local existence, the snapshot drop is the logout-time bundle
// wipe that must NOT take unsynced regulatory work's rendering context with
// it.

// The columns each residue read needs; the full row shapes belong to the
// group factories that own the stores (the PendingAssignmentRow precedent).
// All three assignment-keyed queues share this pair.
interface QueueStateRow {
  assignment_id: string;
  state: string;
}

interface SupplementStateRow {
  template_version_id: string;
  state: string;
}

interface DraftKeyRow {
  assignment_id: string;
}

interface PinnableAssignmentRow {
  id: string;
  template_version_id: string;
}

interface PinnableVersionRow {
  id: string;
}

interface PinnableSupplementRow {
  template_version_id: string;
}

// Bundle stores the drop clears wholesale, the stored catalogs with them
// (reference data, re-pulled on the next field sync); assignments, template
// versions and supplements are pruned by pin instead, and the queues are never
// touched.
const DROP_CLEARED_STORES = [
  "bundle_targets",
  "bundle_kits",
  "bundle_facilities",
  "bundle_walkup_templates",
  "bundle_requirements",
  "bundle_category_templates",
  "bundle_change_request_sets",
  "bundle_catalog_versions",
  "catalogs",
  "sync_meta",
] as const;

const DROP_TX_STORES = [
  "bundle_assignments",
  "bundle_template_versions",
  "bundle_supplements",
  ...DROP_CLEARED_STORES,
  "outbox_submissions",
  "outbox_assignments",
  "outbox_transitions",
  "outbox_supplements",
  "drafts",
] as const;

// The five outbox queues the drained-ness probe reads state from; the three
// row stores below join them in the transaction but count by mere existence.
const UNSYNCED_QUEUE_STORES = [
  "outbox_submissions",
  "outbox_assignments",
  "outbox_transitions",
  "outbox_supplements",
  "outbox_change_request_sets",
] as const;

// Staged evidence counts through its OWN row, not through its draft.
// `draft_attachments` rows do not reference `drafts`, and the capture path
// writes them in the order that makes the gap real: the port stages the bytes
// and inserts the attachment row, then routes the answer change to a DEBOUNCED
// saveDraft. Inside that window — or forever, if the tab dies in it — an
// attachment row and its staged bytes exist with no draft row at all. Reading
// only `drafts` calls that state drained, and the purge then deletes the row
// AND the blob: a compliance photo destroyed with nothing on screen to say so.
//
// The `evidence` store is deliberately NOT counted, and the two adapters agree
// on that: the device's evidence domain is a filesystem tree with no table to
// count, so the ROW is the only unit of retained work both can state
// identically. It is also the only safe one here — `removeDraftAttachment` and
// `deleteDraft` drop rows and leave the bytes to the evidence store's cleanup,
// so orphaned blobs are ordinary residue, and a predicate counting them would
// read true forever and permanently disable the very purge that reclaims them.
// A row with no bytes is already a loud failure (`readEvidencePart` rejects),
// so the row is the authoritative claim on staged evidence.
// A draft sidecar counts the same way: staged requests are unsent work.
const UNSYNCED_ROW_STORES: readonly string[] = ["drafts", "draft_sidecars", "draft_attachments"];

interface StateOnlyRow {
  state: string;
}

export function createLifecycleStore(db: IDBDatabase): LifecycleStore {
  return {
    // One readonly transaction over all eight families so the verdict is a
    // consistent snapshot: any queue row not yet "synced" (error/revoked
    // parks included — retained regulatory state), or any draft, sidecar or
    // staged draft-attachment row at all.
    async hasUnsyncedWork() {
      const tx = db.transaction([...UNSYNCED_QUEUE_STORES, ...UNSYNCED_ROW_STORES], "readonly");
      for (const name of UNSYNCED_QUEUE_STORES) {
        const rows = await getAllRows<StateOnlyRow>(tx.objectStore(name));
        if (rows.some((row) => row.state !== "synced")) return true;
      }
      for (const name of UNSYNCED_ROW_STORES) {
        const rows = await getAllRows<unknown>(tx.objectStore(name));
        if (rows.length > 0) return true;
      }
      return false;
    },

    /**
     * Clears the bundle snapshot EXCEPT the rows unsynced work still
     * references. The sealed outbox document is rendered FROM the bundle's
     * version definition, so dropping the whole snapshot would strand queued
     * capture unrenderable; the pinned residue purges when the queue drains,
     * and everything else re-pulls on the next authenticated sync. A queued
     * supplement references a company-private template version the same way a
     * queued submission references its assignment's version, so its
     * bundle_supplements rows pin by the same argument.
     *
     * The residue: pinned assignment ids = every unsynced row in the three
     * assignment-keyed queues — submissions, walk-up creates and transitions
     * — UNION every draft's key. A queued walk-up create references its
     * locally-inserted assignment row before the server has ever seen it, so
     * dropping that row would leave the pending walk-up unopenable offline
     * until the create drains. Pinned version ids = the template_version_id
     * of every pinned bundle_assignments row; pinned supplement version ids =
     * the template_version_id of every unsynced outbox_supplements row.
     * Drafts, draft sidecars, draft attachments, the outbox queues (sets
     * included) and staged evidence are NEVER touched here — they are the
     * regulatory promise.
     */
    async dropBundleSnapshotPreservingUnsyncedResidue() {
      const tx = db.transaction([...DROP_TX_STORES], "readwrite");
      const submissions = await getAllRows<QueueStateRow>(tx.objectStore("outbox_submissions"));
      const creates = await getAllRows<QueueStateRow>(tx.objectStore("outbox_assignments"));
      const transitions = await getAllRows<QueueStateRow>(tx.objectStore("outbox_transitions"));
      const drafts = await getAllRows<DraftKeyRow>(tx.objectStore("drafts"));
      const unsyncedIds = (rows: QueueStateRow[]): string[] =>
        rows.filter((r) => r.state !== "synced").map((r) => r.assignment_id);
      const pinnedAssignmentIds = new Set<string>([
        ...unsyncedIds(submissions),
        ...unsyncedIds(creates),
        ...unsyncedIds(transitions),
        ...drafts.map((r) => r.assignment_id),
      ]);
      const assignments = tx.objectStore("bundle_assignments");
      const assignmentRows = await getAllRows<PinnableAssignmentRow>(assignments);
      const pinnedVersionIds = new Set(
        assignmentRows
          .filter((r) => pinnedAssignmentIds.has(r.id))
          .map((r) => r.template_version_id),
      );
      const supplements = await getAllRows<SupplementStateRow>(
        tx.objectStore("outbox_supplements"),
      );
      const pinnedSupplementVersionIds = new Set(
        supplements.filter((r) => r.state !== "synced").map((r) => r.template_version_id),
      );
      for (const name of DROP_CLEARED_STORES) {
        tx.objectStore(name).clear();
      }
      await deleteRowsWhere<PinnableAssignmentRow>(
        assignments,
        (r) => !pinnedAssignmentIds.has(r.id),
      );
      await deleteRowsWhere<PinnableVersionRow>(
        tx.objectStore("bundle_template_versions"),
        (r) => !pinnedVersionIds.has(r.id),
      );
      await deleteRowsWhere<PinnableSupplementRow>(
        tx.objectStore("bundle_supplements"),
        (r) => !pinnedSupplementVersionIds.has(r.template_version_id),
      );
      await txDone(tx);
    },

    // Empties every store, staged evidence included — the drained-queue
    // logout that ends a profile's local existence on this machine.
    async purgeProfileDatabase() {
      const names = Array.from(db.objectStoreNames);
      const tx = db.transaction(names, "readwrite");
      for (const name of names) {
        tx.objectStore(name).clear();
      }
      await txDone(tx);
    },
  };
}
